import { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  CircularProgress,
  Alert,
} from '@mui/material';
import { PlayArrow as PlayIcon } from '@mui/icons-material';
import api from '../services/api';
import toast from 'react-hot-toast';
import ConfigCard from '../components/AutoLiker/ConfigCard';
import AccountFilterBar from '../components/common/AccountFilterBar';
import { useAutoLikerWebSocket, type AutoLikerProgress } from '../hooks/useAutoLikerWebSocket';

export default function AutoLikerPage() {
  const [configs, setConfigs] = useState<any[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [filterAccountId, setFilterAccountId] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [progressMap, setProgressMap] = useState<Record<number, AutoLikerProgress>>({});
  const [formData, setFormData] = useState({
    accountId: '',
    name: '',
    keywords: '',
    maxLikesPerDay: 50,
    // "За какие дни" — 0 = без ограничения. 7/14/30/90 — N последних дней.
    postAgeDays: 7 as 0 | 7 | 14 | 30 | 90,
  });

  const loadData = async () => {
    setLoading(true);
    try {
      const [cfgRes, accRes] = await Promise.all([
        api.get('/auto-liker'),
        api.get('/vk-accounts'),
      ]);
      setConfigs(cfgRes.data.data.configs);
      setAccounts(accRes.data.data.accounts);
    } catch (err) {
      toast.error('Не удалось загрузить данные');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // WebSocket handlers
  const handleAction = useCallback((data: any) => {
    console.log('New action received:', data);

    // Update config stats
    setConfigs((prev) =>
      prev.map((cfg) =>
        cfg.id === data.configId
          ? { ...cfg, todayLikes: cfg.todayLikes + 1, totalLikes: cfg.totalLikes + 1 }
          : cfg
      )
    );

    // Add action to the list
    const addActionFn = (window as any)[`addActionToConfig${data.configId}`];
    if (addActionFn) {
      addActionFn({ ...data.action, vkData: data.vkData });
    }

    toast.success(`Новый лайк: пост от ${data.vkData?.authorName || 'неизвестного автора'}`);
  }, []);

  const handleStatus = useCallback((data: any) => {
    console.log('Status update:', data);
    setConfigs((prev) =>
      prev.map((cfg) =>
        cfg.id === data.configId
          ? { ...cfg, todayLikes: data.todayLikes, isActive: data.isActive }
          : cfg
      )
    );
  }, []);

  const handleError = useCallback((data: any) => {
    console.log('Error:', data);
    toast.error(`Правило #${data.configId}: ${data.error}`);
  }, []);

  const handleProgress = useCallback((data: AutoLikerProgress) => {
    console.log('Progress update:', data);
    setProgressMap((prev) => ({
      ...prev,
      [data.configId]: data,
    }));

    // Clear completed/error progress after 30 seconds
    if (data.phase === 'completed' || data.phase === 'error') {
      setTimeout(() => {
        setProgressMap((prev) => {
          const newMap = { ...prev };
          if (newMap[data.configId]?.phase === data.phase) {
            delete newMap[data.configId];
          }
          return newMap;
        });
      }, 30000);
    }
  }, []);

  useAutoLikerWebSocket({
    onAction: handleAction,
    onStatus: handleStatus,
    onError: handleError,
    onProgress: handleProgress,
  });

  const handleCreate = async () => {
    try {
      const payload: any = {
        accountId: parseInt(formData.accountId),
        name: formData.name,
        keywords: formData.keywords
          .split(',')
          .map((k) => k.trim())
          .filter((k) => k.length > 0),
        maxLikesPerDay: formData.maxLikesPerDay,
      };
      if (formData.postAgeDays > 0) {
        // Backend expects ISO datetime string (zod .datetime()). Compute
        // "now - N days" right before sending.
        const minDate = new Date(Date.now() - formData.postAgeDays * 24 * 60 * 60 * 1000);
        payload.minPostDate = minDate.toISOString();
      }
      await api.post('/auto-liker', payload);
      toast.success('Правило создано!');
      setOpen(false);
      setFormData({
        accountId: '',
        name: '',
        keywords: '',
        maxLikesPerDay: 50,
        postAgeDays: 7,
      });
      loadData();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Не удалось создать правило');
    }
  };

  const handleRunAll = async () => {
    setRunning(true);
    try {
      const res = await api.post('/auto-liker/run-all');
      const { queued, total } = res.data.data;
      toast.success(`Запущено ${queued} из ${total} активных правил`);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Не удалось запустить');
    } finally {
      setRunning(false);
    }
  };

  const handleDelete = (id: number) => {
    setConfigs((prev) => prev.filter((cfg) => cfg.id !== id));
  };

  const visibleConfigs = filterAccountId
    ? configs.filter((cfg) => String(cfg.accountId) === filterAccountId)
    : configs;
  const activeCount = configs.filter((cfg) => cfg.isActive).length;

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Автолайкер</Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={running ? <CircularProgress size={20} /> : <PlayIcon />}
            onClick={handleRunAll}
            disabled={running || activeCount === 0}
          >
            Запустить все активные ({activeCount})
          </Button>
          <Button variant="contained" onClick={() => setOpen(true)}>
            Создать правило
          </Button>
        </Box>
      </Box>

      <AccountFilterBar accounts={accounts} value={filterAccountId} onChange={setFilterAccountId} />

      {configs.length === 0 ? (
        <Alert severity="info">
          Пока нет ни одного правила. Создайте первое правило автолайкера, чтобы начать.
        </Alert>
      ) : visibleConfigs.length === 0 ? (
        <Alert severity="info">Нет правил для выбранного аккаунта.</Alert>
      ) : (
        <Box>
          {visibleConfigs.map((cfg) => (
            <ConfigCard
              key={cfg.id}
              config={cfg}
              onUpdate={loadData}
              onDelete={handleDelete}
              progress={progressMap[cfg.id] || null}
            />
          ))}
        </Box>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Новое правило автолайкера</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            label="VK Аккаунт"
            value={formData.accountId}
            onChange={(e) => setFormData({ ...formData, accountId: e.target.value })}
            margin="normal"
            SelectProps={{ native: true }}
          >
            <option value="">Выберите аккаунт</option>
            {accounts.map((acc) => (
              <option key={acc.id} value={acc.id}>
                {acc.firstName} {acc.lastName}
              </option>
            ))}
          </TextField>
          <TextField
            fullWidth
            label="Название правила"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Ключевые слова (через запятую)"
            value={formData.keywords}
            onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
            margin="normal"
            helperText='Лайкаются только посты, в которых присутствуют ВСЕ слова (логика "И"). Пример: "мы, открылись" → пост должен содержать оба слова.'
          />
          <TextField
            fullWidth
            type="number"
            label="Макс. лайков в день"
            value={formData.maxLikesPerDay}
            onChange={(e) =>
              setFormData({ ...formData, maxLikesPerDay: parseInt(e.target.value) })
            }
            margin="normal"
            inputProps={{ min: 1, max: 200 }}
          />
          <TextField
            select
            fullWidth
            label="Возраст постов"
            value={formData.postAgeDays}
            onChange={(e) =>
              setFormData({
                ...formData,
                postAgeDays: parseInt(e.target.value) as 0 | 7 | 14 | 30 | 90,
              })
            }
            margin="normal"
            SelectProps={{ native: true }}
            helperText="Лайкать только посты не старше выбранного периода"
          >
            <option value={7}>За последние 7 дней</option>
            <option value={14}>За последние 14 дней</option>
            <option value={30}>За последние 30 дней</option>
            <option value={90}>За последние 90 дней</option>
            <option value={0}>Без ограничения</option>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Отмена</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={
              !formData.accountId || !formData.name || !formData.keywords
            }
          >
            Создать
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
